import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon, NotificationsNoneOutlined } from '@mui/icons-material';
import { authService } from '@root/app/shared/new-services/auth.service';
import { companyService } from '@root/app/shared/new-services/company.service';
import { useTranslation } from '@root/app/shared/hooks/useTranslation';
import HfUserProfileMenu from '@root/app/shared/new-components/hf-user-profile-menu/hfUserProfileMenu';
import { toggleMiniNavbar } from '@root/app/shared/utils/sidebar.utils';

declare global {
    interface Window {
        beamer_config?: Record<string, unknown>;
        Beamer?: { init: () => void };
    }
}

const HfTopnavbar: React.FC = () => {
    const { t } = useTranslation();
    const isDesktop = useMediaQuery('(min-width:1024px)');
    const [companyName, setCompanyName] = useState<string | null>(null);
    const user = authService.user();

    useEffect(() => {
        companyService.getUserCompany((err, company) => {
            if (!err && company) {
                setCompanyName(company.name ?? null);
            }
        });
    }, []);

    useEffect(() => {
        if (!user?.id || !window.beamer_config) {
            return;
        }

        window.beamer_config.top = -10;
        window.beamer_config.icon = 'flame';
        window.beamer_config.right = -10;
        window.beamer_config.selector = 'getbeamer';
        window.beamer_config.user_firstname = user.name;
        window.beamer_config.user_lastname = '';
        window.beamer_config.user_email = user.email;
        window.beamer_config.user_id = user.id;
        window.Beamer?.init();
    }, [user?.email, user?.id, user?.name]);

    return (
        <Box
            sx={(theme) => ({
                m: 0,
                width: '100%',
                bgcolor: theme.palette.hfLayout.paper,
                borderBottom: `1px solid ${theme.palette.hfLayout.border}`
            })}
        >
            <Box
                component='nav'
                role='navigation'
                sx={{ mb: 0, width: '100%' }}
            >
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        minHeight: theme.hfLayout.sizes.navBarHeight
                    })}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                        <Box
                            sx={(theme) => ({
                                height: theme.hfLayout.sizes.navBarHeight,
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0
                            })}
                        >
                            <IconButton
                                onClick={toggleMiniNavbar}
                                aria-label='Toggle navigation menu'
                                size='small'
                                sx={{ ml: 0.5, color: 'primary.main' }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>

                        {!isDesktop && (
                            <Typography
                                component='h3'
                                variant='h6'
                                noWrap
                                sx={{
                                    display: { xs: 'none', sm: 'block', lg: 'none' },
                                    m: 0,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {companyName ?? t('UNKNOWN_NAME')}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Box
                            sx={(theme) => ({
                                height: theme.hfLayout.sizes.navBarHeight,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRight: `1px solid ${theme.palette.hfLayout.border}`,
                                px: 1.5
                            })}
                        >
                            <Box
                                id='getbeamer'
                                aria-hidden='true'
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: 'text.secondary',
                                    '& .beamer_icon': {
                                        width: '1.25rem !important',
                                        height: '1.25rem !important',
                                        fontSize: '1.25rem !important'
                                    }
                                }}
                            >
                                <Typography
                                    component='span'
                                    variant='body2'
                                    color='inherit'
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        lineHeight: 1,
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    <NotificationsNoneOutlined sx={{ fontSize: '1.25rem' }} />
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={(theme) => ({
                                height: theme.hfLayout.sizes.navBarHeight,
                                display: 'flex',
                                alignItems: 'center',
                                px: 1
                            })}
                        >
                            <HfUserProfileMenu />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default HfTopnavbar;
